import { HHStack, HHTextField } from '@hinge-health/react-component-library';
import { useState } from 'react';
import { parseJsonObject } from '../utils/json.utils';

export const JSON_EDITOR_TEST_ID = 'pdg-json-editor';
export const JSON_EDITOR_INPUT_TEST_ID = 'pdg-json-editor-input';
export const JSON_EDITOR_ERROR_TEST_ID = 'pdg-json-editor-error';

export const JSON_EDITOR_DEFAULT_ROWS = 10;

export interface JsonEditorProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  /**
   * Error owned by the parent — typically raised when the form is submitted
   * with text the user never blurred out of. Takes precedence over the
   * editor's own blur validation so the two never disagree.
   */
  error?: string | null;
  helperText?: string;
  disabled?: boolean;
  /**
   * Selectable and copyable, but not editable. Unlike `disabled`, the text
   * stays readable and can be selected (e.g. viewing a configuration the
   * operator may not change).
   */
  readOnly?: boolean;
  name?: string;
  testId?: string;
}

/**
 * Plain-text editor for a JSONB payload (today: a configuration's Hyrule
 * `definition`).
 *
 * v1 deliberately edits raw JSON rather than offering a rule builder: PDGS
 * owns the rule grammar and it is still moving, so a structured editor here
 * would silently drop any construct it did not know about. Validation runs
 * on blur so a half-typed object is not flagged on every keystroke.
 */
export const JsonEditor = ({
  label,
  value,
  onChange,
  error = null,
  helperText,
  disabled = false,
  readOnly = false,
  name = 'json',
  testId = JSON_EDITOR_TEST_ID,
}: JsonEditorProps): JSX.Element => {
  const [blurError, setBlurError] = useState<string | null>(null);

  const handleBlur = (): void => {
    setBlurError(parseJsonObject(value).error);
  };

  const visibleError = error ?? blurError;

  /*
   * The error goes through `helperText` rather than a separate element so
   * MUI owns the `aria-describedby` link between the textarea and the
   * message — a sibling paragraph is announced only if the user happens to
   * browse past it. This matches the Name / Data provider fields on the
   * configuration form. `role="alert"` keeps the message announced when it
   * appears after a blur; the test id stays on the same node so it still
   * identifies the error text.
   */
  return (
    <HHStack gap={1} data-testid={testId}>
      <HHTextField
        hhVariant="variant-bypass"
        variant="outlined"
        label={label}
        value={value}
        multiline
        minRows={JSON_EDITOR_DEFAULT_ROWS}
        disabled={disabled}
        error={Boolean(visibleError)}
        helperText={visibleError ?? helperText}
        FormHelperTextProps={
          visibleError
            ? {
                role: 'alert',
                'data-testid': `${testId}-error`,
              }
            : undefined
        }
        onChange={(event): void => onChange(event.target.value)}
        onBlur={handleBlur}
        inputProps={{
          name,
          readOnly,
          role: 'textbox',
          'aria-label': label,
          'data-testid': `${testId}-input`,
          style: { fontFamily: 'monospace' },
        }}
      />
    </HHStack>
  );
};
